import {t, Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import {DeepReadonly} from 'utility-types';

import {Handler, HandlerOf, RendererOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dropdown, DropdownProps} from '../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {DataFieldsTableColumnModel} from '../../models/api/dataFieldsTableModel';
import {DialogButtonGroup} from '../common/dialogs/dialogButtonGroup';
import {DialogContent} from '../common/dialogs/dialogContent';
import {DialogHeader} from '../common/dialogs/dialogHeader';

interface RuleEditorDataTableColumnDialogProps extends DropdownProps {
  mode: 'create' | 'edit';
  initialData?: DeepReadonly<Partial<DataFieldsTableColumnModel>>;
  onSubmit: HandlerOf<Partial<DataFieldsTableColumnModel>>;
  columnValidator: RendererOf<Partial<DataFieldsTableColumnModel>>;
  onDelete?: Handler;
}

export const DataFieldsTableColumnDialog: FC<RuleEditorDataTableColumnDialogProps> = ({
  mode,
  onSubmit,
  onDelete,
  columnValidator,
  initialData = {},
  ...dialogProps
}) => {
  const [name, setName] = useState(initialData?.name ?? '');
  const localize = useLocalize();
  const errorMessage = columnValidator({name, id: initialData?.id});
  const hasError = Boolean(errorMessage);

  return (
    <Dropdown {...dialogProps} width={300}>
      <DialogHeader
        title={
          mode === 'create' ? (
            <Trans id="rule-editor-data-table-add-column-dialog-title-create">Add column</Trans>
          ) : (
            <Trans id="rule-editor-data-table-add-column-dialog-title-edit">Edit column</Trans>
          )
        }
      />
      <DialogContent>
        <TextBox
          label={<Trans id="rule-editor-data-table-add-column-name-field-label">Name</Trans>}
          value={name}
          placeholder={localize(t('rule-editor-data-table-add-column-name-field-placeholder')`Column name`)}
          onChange={(value) => setName(value)}
          isRequired
          noMargin
          shouldAutoFocus
          errorMessage={errorMessage}
          hasError={hasError}
        />
      </DialogContent>
      <DialogButtonGroup>
        {mode === 'edit' && onDelete && (
          <Button
            variant={ButtonVariantsEnum.SECONDARY_DANGER}
            onClick={() => {
              onDelete();
              dialogProps.onRequestClose?.();
            }}
          >
            <Trans id="rule-editor-data-table-add-column-delete">Delete</Trans>
          </Button>
        )}
        <ButtonGroupSpacer />
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={() => dialogProps.onRequestClose?.()}>
          <Trans id="rule-editor-data-table-add-column-cancel">Cancel</Trans>
        </Button>
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          onClick={() => {
            onSubmit?.({name});
            dialogProps.onRequestClose?.();
          }}
          isDisabled={name === undefined || hasError}
        >
          {mode === 'create' ? (
            <Trans id="rule-editor-data-table-add-column-buttons-create">Create</Trans>
          ) : (
            <Trans id="rule-editor-data-table-add-column-buttons-edit">Save</Trans>
          )}
        </Button>
      </DialogButtonGroup>
    </Dropdown>
  );
};
